import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight, Download, Copy, Printer, Columns } from 'lucide-react'
import toast from 'react-hot-toast'
import { showDeleteConfirm, showSuccess, showError } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { empMediaLinksService } from '@/services/empMediaLinksService'
import { contractService } from '@/services/contractService'
import type { MediaLinksListItem } from '@/api/types/empMediaLinks.types'
import type { EmployeeOption } from '@/api/types'

function formatDate(d?: string): string {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
  } catch {
    return String(d).slice(0, 10)
  }
}

function linkDisplay(url?: string): string {
  if (!url || !url.trim()) return '—'
  return url.length > 40 ? url.slice(0, 40) + '…' : url
}

export const MediaLinksList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [list, setList] = useState<MediaLinksListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    employeeName: true,
    twitterLink: true,
    linkedInLink: true,
    instagramLink: true,
    facebookLink: true,
    websiteLink: true,
    otherLink: true,
    createdDate: true,
    action: true,
  })

  useEffect(() => {
    if (!branchId) return
    contractService.getAllEmployees(branchId).then((data) => setEmployees(data ?? [])).catch(() => setEmployees([]))
  }, [branchId])

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const res = await empMediaLinksService.getAll(branchId, 1, 500)
        setList(res.data ?? [])
        setTotalRecords(res.totalRecords ?? 0)
      } catch (e: any) {
        const status = e?.response?.status
        const msg = (e?.response?.data?.message ?? e?.message ?? '').toLowerCase()
        const isNoData = status === 404 || /no data|not found|no records/.test(msg)
        setList([])
        setTotalRecords(0)
        if (!isNoData) toast.error(e?.response?.data?.message ?? t('mediaLinks.fetchFailed'))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId])

  const getEmployeeId = (row: MediaLinksListItem): number | undefined => row.empId ?? row.employeeId

  const getEmployeeName = (empId?: number): string => {
    if (!empId) return '—'
    const emp = employees.find((e) => e.empId === empId)
    if (!emp) return '—'
    return (
      (emp.fullName ?? [emp.empFirstNameEn, emp.empSecondNameEn, emp.empLastNameEn].filter(Boolean).join(' ').trim()) ||
      t('mediaLinks.employeeFallback', { id: empId })
    )
  }

  const getLinkedIn = (row: MediaLinksListItem): string => row.linkedInLink ?? row.linkedinLink ?? ''

  const filteredList = useMemo(() => {
    if (!searchQuery.trim()) return list
    const q = searchQuery.toLowerCase()
    return list.filter(
      (r) =>
        getEmployeeName(getEmployeeId(r)).toLowerCase().includes(q) ||
        (r.twitterLink ?? '').toLowerCase().includes(q) ||
        getLinkedIn(r).toLowerCase().includes(q) ||
        (r.instagramLink ?? '').toLowerCase().includes(q) ||
        (r.facebookLink ?? '').toLowerCase().includes(q) ||
        (r.websiteLink ?? '').toLowerCase().includes(q) ||
        (r.otherLink ?? '').toLowerCase().includes(q)
    )
  }, [list, searchQuery, employees, t])

  const paginatedRows = useMemo(
    () => filteredList.slice((pageNumber - 1) * pageSize, pageNumber * pageSize),
    [filteredList, pageNumber, pageSize]
  )
  const filteredTotal = filteredList.length
  const totalPages = Math.max(1, Math.ceil(filteredTotal / pageSize))
  const visibleColumnCount =
    [columnVisibility.employeeName, columnVisibility.twitterLink, columnVisibility.linkedInLink, columnVisibility.instagramLink, columnVisibility.facebookLink, columnVisibility.websiteLink, columnVisibility.otherLink, columnVisibility.createdDate, columnVisibility.action].filter(Boolean).length || 1

  const handleAdd = () => navigate('/media-links/new')
  const handleEdit = (id: number) => navigate(`/media-links/${id}/edit`)

  const handleDelete = async (id: number) => {
    const confirmed = await showDeleteConfirm(t('mediaLinks.deleteTitle'), t('mediaLinks.deleteConfirm'))
    if (!confirmed) return
    setDeletingId(id)
    try {
      await empMediaLinksService.delete(id)
      await showSuccess(t('mediaLinks.deletedTitle'), t('mediaLinks.deletedMessage'))
      if (branchId) {
        const res = await empMediaLinksService.getAll(branchId, 1, 500)
        setList(res.data ?? [])
        setTotalRecords(res.totalRecords ?? 0)
      }
    } catch (e: any) {
      await showError(t('mediaLinks.deleteFailedTitle'), e?.response?.data?.message ?? e?.message ?? t('mediaLinks.deleteFailedMessage'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleCsv = () => {
    const headers = [
      t('mediaLinks.columnEmployeeName'),
      t('mediaLinks.columnTwitterLink'),
      t('mediaLinks.columnLinkedInLink'),
      t('mediaLinks.columnInstagramLink'),
      t('mediaLinks.columnFacebookLink'),
      t('mediaLinks.columnWebsiteLink'),
      t('mediaLinks.columnOtherLink'),
      t('mediaLinks.columnCreatedDate'),
    ]
    const rows = filteredList.map((r) =>
      [
        getEmployeeName(getEmployeeId(r)),
        r.twitterLink ?? '',
        getLinkedIn(r),
        r.instagramLink ?? '',
        r.facebookLink ?? '',
        r.websiteLink ?? '',
        r.otherLink ?? '',
        r.createdDate ? formatDate(r.createdDate) : '',
      ].map((c) => (String(c).includes(',') ? `"${String(c).replace(/"/g, '""')}"` : c)).join(',')
    )
    const csv = [headers.join(','), ...rows].join('\r\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = t('mediaLinks.csvFileName')
    a.click()
    URL.revokeObjectURL(url)
    toast.success(t('mediaLinks.csvDownloaded'))
  }

  const handleCopy = () => {
    const text = filteredList
      .map((r) =>
        [getEmployeeName(getEmployeeId(r)), r.twitterLink ?? '', getLinkedIn(r), r.instagramLink ?? '', r.facebookLink ?? '', r.websiteLink ?? '', r.otherLink ?? '', r.createdDate ? formatDate(r.createdDate) : ''].join('\t')
      )
      .join('\n')
    navigator.clipboard.writeText(text).then(() => toast.success(t('mediaLinks.copiedToClipboard')))
  }

  const handlePrint = () => window.print()

  const paginationFrom = filteredTotal === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  const paginationTo = Math.min(pageNumber * pageSize, filteredTotal)

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('mediaLinks.listTitle')} description={t('mediaLinks.listDescription')} />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">{t('mediaLinks.selectBranchFirst')}</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('mediaLinks.listTitle')}
        description={t('mediaLinks.listDescription')}
        action={{ label: t('mediaLinks.actionAdd'), onClick: handleAdd, icon: Plus }}
      />
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('mediaLinks.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Columns className="w-4 h-4 mr-2" />
                  {t('mediaLinks.columnVisibility')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[180px]">
                <DropdownMenuCheckboxItem checked={columnVisibility.employeeName} onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, employeeName: !!v }))}>{t('mediaLinks.columnEmployeeName')}</DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem checked={columnVisibility.twitterLink} onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, twitterLink: !!v }))}>{t('mediaLinks.columnTwitterLink')}</DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem checked={columnVisibility.linkedInLink} onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, linkedInLink: !!v }))}>{t('mediaLinks.columnLinkedInLink')}</DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem checked={columnVisibility.instagramLink} onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, instagramLink: !!v }))}>{t('mediaLinks.columnInstagramLink')}</DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem checked={columnVisibility.facebookLink} onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, facebookLink: !!v }))}>{t('mediaLinks.columnFacebookLink')}</DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem checked={columnVisibility.websiteLink} onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, websiteLink: !!v }))}>{t('mediaLinks.columnWebsiteLink')}</DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem checked={columnVisibility.otherLink} onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, otherLink: !!v }))}>{t('mediaLinks.columnOtherLink')}</DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem checked={columnVisibility.createdDate} onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, createdDate: !!v }))}>{t('mediaLinks.columnCreatedDate')}</DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem checked={columnVisibility.action} onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, action: !!v }))}>{t('mediaLinks.columnAction')}</DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" onClick={handleCsv}><Download className="w-4 h-4 mr-2" />{t('mediaLinks.csv')}</Button>
            <Button variant="outline" size="sm" onClick={handleCopy}><Copy className="w-4 h-4 mr-2" />{t('mediaLinks.copy')}</Button>
            <Button variant="outline" size="sm" onClick={handlePrint}><Printer className="w-4 h-4 mr-2" />{t('mediaLinks.print')}</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {columnVisibility.employeeName && <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('mediaLinks.columnEmployeeName')}</TableHead>}
                  {columnVisibility.twitterLink && <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('mediaLinks.columnTwitterLink')}</TableHead>}
                  {columnVisibility.linkedInLink && <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('mediaLinks.columnLinkedInLink')}</TableHead>}
                  {columnVisibility.instagramLink && <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('mediaLinks.columnInstagramLink')}</TableHead>}
                  {columnVisibility.facebookLink && <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('mediaLinks.columnFacebookLink')}</TableHead>}
                  {columnVisibility.websiteLink && <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('mediaLinks.columnWebsiteLink')}</TableHead>}
                  {columnVisibility.otherLink && <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('mediaLinks.columnOtherLink')}</TableHead>}
                  {columnVisibility.createdDate && <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('mediaLinks.columnCreatedDate')}</TableHead>}
                  {columnVisibility.action && <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('mediaLinks.columnAction')}</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="text-center py-8 text-muted-foreground">{t('mediaLinks.loading')}</TableCell>
                  </TableRow>
                ) : paginatedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="text-center py-8 text-muted-foreground">{t('mediaLinks.noData')}</TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map((row) => (
                    <TableRow key={row.id}>
                      {columnVisibility.employeeName && <TableCell className="px-4 py-2 text-sm text-center align-middle">{getEmployeeName(getEmployeeId(row))}</TableCell>}
                      {columnVisibility.twitterLink && <TableCell className="px-4 py-2 text-sm text-center align-middle max-w-[180px] truncate" title={row.twitterLink ?? ''}>{linkDisplay(row.twitterLink)}</TableCell>}
                      {columnVisibility.linkedInLink && <TableCell className="px-4 py-2 text-sm text-center align-middle max-w-[180px] truncate" title={getLinkedIn(row)}>{linkDisplay(getLinkedIn(row))}</TableCell>}
                      {columnVisibility.instagramLink && <TableCell className="px-4 py-2 text-sm text-center align-middle max-w-[180px] truncate" title={row.instagramLink ?? ''}>{linkDisplay(row.instagramLink)}</TableCell>}
                      {columnVisibility.facebookLink && <TableCell className="px-4 py-2 text-sm text-center align-middle max-w-[180px] truncate" title={row.facebookLink ?? ''}>{linkDisplay(row.facebookLink)}</TableCell>}
                      {columnVisibility.websiteLink && <TableCell className="px-4 py-2 text-sm text-center align-middle max-w-[180px] truncate" title={row.websiteLink ?? ''}>{linkDisplay(row.websiteLink)}</TableCell>}
                      {columnVisibility.otherLink && <TableCell className="px-4 py-2 text-sm text-center align-middle max-w-[180px] truncate" title={row.otherLink ?? ''}>{linkDisplay(row.otherLink)}</TableCell>}
                      {columnVisibility.createdDate && <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatDate(row.createdDate)}</TableCell>}
                      {columnVisibility.action && (
                        <TableCell className="px-4 py-2 text-center align-middle">
                          <div className="flex justify-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(row.id!)} className="h-8 w-8 p-0"><Edit className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id!)} disabled={deletingId === row.id} className="h-8 w-8 p-0 text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
            <div className="text-sm text-muted-foreground">
              {t('mediaLinks.paginationShowing', { from: paginationFrom, to: paginationTo, total: filteredTotal })}
            </div>
            <div className="flex items-center gap-2">
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPageNumber(1) }}>
                <SelectTrigger className="w-[90px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" disabled={pageNumber <= 1 || loading} onClick={() => setPageNumber((p) => Math.max(1, p - 1))}>
                <ChevronLeft className="h-4 w-4" /> {t('mediaLinks.previous')}
              </Button>
              <span className="text-sm text-muted-foreground px-2">{t('mediaLinks.pageOf', { page: pageNumber, totalPages })}</span>
              <Button variant="outline" size="sm" disabled={pageNumber >= totalPages || loading || totalRecords === 0} onClick={() => setPageNumber((p) => p + 1)}>
                {t('mediaLinks.next')} <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
